
import { Chart, ChartDataset, ScatterDataPoint } from "chart.js";
import { defineComponent, shallowRef } from 'vue';
import { mapState } from 'pinia';
import { NPopover } from "naive-ui";

import ComponentBase from "../component-base";
import { useAdvancedViewStore } from '~/store/advanced-view.store';
import { Station } from "~/api/models/station.model";

import { ChartConfig, ChartDatasetConfig } from './chart.config';

export default defineComponent({

    extends: ComponentBase,

    components: {
        NPopover,
    },

    props: {
        indicator: { type: Object, required: true },
        stations: { type: Array, required: true },
    },

    data() {
        return {
            chart: null,
            loading: false,
            colors: ['#1BCF42', '#FF7E00','#F22C13', '#3DDFB4', '#F734D0', '#3c8dbc', '#fad733', '#21998F', '#C479B0', '#0AE1F7', '#CD3C3B', '#181C9A', ],
            config: (ChartConfig[this.indicator.key] || ChartConfig['default'])(this.indicator.key),
            datasetConfigBuilder: (ChartDatasetConfig[this.indicator.key] || ChartDatasetConfig['default']),
            datasets: null,
        };
    },

    computed: {
        ...mapState(useAdvancedViewStore, ['measures', 'since', 'before', 'selected']),

        title(): string {
            return this.indicator.label;
        },

        timeUnit() {
            const delta = this.before.diff(this.since);
            if(delta >  3 * 24 * 60 * 60 * 1000) {
                return 'day';
            } else if(delta >  2 * 60 * 60 * 1000) {
                return 'hour'
            } else {
                return 'minute';
            }
        },

        timeUnitDisplayFormat() {
            const delta = this.before.diff(this.since);
            if(delta >=  7 * 24 * 60 * 60 * 1000) {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            } else if(delta >=  3 * 24 * 60 * 60 * 1000){
                return { day: 'lll', hour: 'lll',  minute: 'HH:mm'};
            } else {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            }
        }
    },

    mounted() {
        const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;

        if (!chartElement) {
            return;
        }

        const chartConfig = this.config;
        this.updateDatasets();
        chartConfig.data = this.datasetConfigBuilder(this.datasets);
        //@ts-ignore
        chartConfig.options.scales.x.time.unit = this.timeUnit;
        chartConfig.options.scales.x.time.displayFormats = this.timeUnitDisplayFormat;
        this.chart = shallowRef(new Chart(chartElement.getContext('2d'), chartConfig));
    },

    methods: {
        updateChart() {
            try {
                this.chart.data = this.datasetConfigBuilder(this.datasets);
                this.chart.update();
            } catch (e) {
                console.error(e);
            }
        },

        updateDatasets(): void {
            this.datasets = Object.keys(this.measures).map((stationId: string, index: number) => {
                const station = this.selected.find((station: Station) => station.id === stationId);
                if (!station) {
                    return null;
                }

                const data: ScatterDataPoint[] = this.measures[stationId][this.indicator.key];
                let color = this.colors[Math.abs(this.hashCode(station.id)) % this.colors.length];
                color = station.metadata['elichens/public/color'] || color;
                return {
                    label: this.getStationName(station),
                    data,
                    borderColor: color,
                    backgroundColor: color,
                    tension: 0.2,
                    cubicInterpolationMode: 'monotone',
                    borderWidth: 1,
                    pointRadius: data && data.length <= 300 ? 2 : 0,
                    pointHitRadius: 10,
                }
            }).filter((cd: ChartDataset) => cd);
        },

        hashCode(str) {
            var hash = 0;
            for (var i = 0; i < str.length; i++) {
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }
            return hash;
        },
    },
});
